<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Match`.trim()" icon="mdi-gamepad-variant-outline" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-calendar-clock</v-icon>Schedule & venue
                </div>
                <v-row align="center">
                    <v-col cols="12" md="6" lg="5">
                        <date-time-picker
                            :date-label="'Match Date'"
                            :date="matchDate"
                            :time-label="'Match Time'"
                            :time="matchTime"
                            :display-time="true"
                            @setDate="setMatchDate"
                            @setTime="setMatchTime"
                        />
                    </v-col>
                    <v-col cols="12" md="6" lg="4">
                        <v-autocomplete
                            v-model="match.arena"
                            outlined
                            dense
                            hide-details="auto"
                            :items="arenaOptions"
                            :item-text="arenaLabel"
                            menu-props="auto"
                            label="Arena"
                            prepend-inner-icon="mdi-stadium-outline"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="12" lg="3">
                        <v-switch
                            v-model="match.postponed"
                            class="mt-0 pt-0"
                            color="primary"
                            hide-details
                            inset
                            :label="match.postponed ? 'Postponed' : 'Not Postponed'"
                        />
                    </v-col>
                    <!-- kept as data, but out of the site, the rankings and the stats (e.g. a match of a team in its previous league) -->
                    <v-col cols="12" lg="5">
                        <v-switch
                            v-model="match.excluded"
                            class="mt-0 pt-0"
                            color="red"
                            inset
                            persistent-hint
                            hint="Hidden from the site, the rankings and the player and team stats. The match and its stats are kept."
                            :label="match.excluded ? 'Excluded' : 'Not Excluded'"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-trophy-outline</v-icon>Competition
                </div>
                <v-row align="center">
                    <v-col cols="12" md="4">
                        <v-switch
                            v-model="match.cup"
                            class="mt-0 pt-0"
                            color="primary"
                            hide-details
                            inset
                            :label="match.cup ? 'Cup match' : 'League match'"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="match.season"
                            outlined
                            dense
                            hide-details="auto"
                            :items="seasons"
                            :item-text="(item) => `${item.year} - ${item.type}`"
                            menu-props="auto"
                            label="Season"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col v-if="!match.cup" cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="match.league"
                            outlined
                            dense
                            hide-details="auto"
                            :items="leagues"
                            item-text="title"
                            menu-props="auto"
                            label="League"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col v-else cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="match.cupLeague"
                            outlined
                            dense
                            hide-details="auto"
                            :items="cupLeagues"
                            item-text="title"
                            menu-props="auto"
                            label="Cup League"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-select
                            v-model="match.phase"
                            outlined
                            dense
                            hide-details="auto"
                            :items="['placements', 'regular_season', 'playoffs']"
                            menu-props="auto"
                            label="Phase"
                            :clearable="!readOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="match.round"
                            outlined
                            dense
                            hide-details="auto"
                            label="Matchday"
                            type="number"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-select
                            v-model="match.customRoundText"
                            outlined
                            dense
                            hide-details="auto"
                            :items="['Quarter Finals', 'Semi Finals', 'Final']"
                            label="Custom Round Text"
                            menu-props="auto"
                            :clearable="clearable"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-scoreboard-outline</v-icon>Teams & score
                </div>
                <v-row class="mx-0 score-row" align="center">
                    <v-col cols="12" sm="8" lg="4">
                        <v-autocomplete
                            v-model="match.team1"
                            outlined
                            dense
                            hide-details="auto"
                            :items="teamOptions"
                            item-text="name"
                            menu-props="auto"
                            label="Team 1"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="12" sm="4" lg="1">
                        <v-text-field
                            v-model="match.scoreTeam1"
                            outlined
                            dense
                            hide-details="auto"
                            type="number"
                            label="Score"
                            class="score-input"
                        />
                    </v-col>
                    <v-col cols="12" lg="2" align="center" class="text-h6 font-weight-bold grey--text py-0 py-lg-3">
                        VS
                    </v-col>
                    <v-col cols="12" sm="4" lg="1" order="2" order-sm="1">
                        <v-text-field
                            v-model="match.scoreTeam2"
                            outlined
                            dense
                            hide-details="auto"
                            type="number"
                            label="Score"
                            class="score-input"
                        />
                    </v-col>
                    <v-col cols="12" sm="8" lg="4" order="1" order-sm="2">
                        <v-autocomplete
                            v-model="match.team2"
                            outlined
                            dense
                            hide-details="auto"
                            :items="teamOptions"
                            item-text="name"
                            menu-props="auto"
                            label="Team 2"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-file-document-multiple-outline</v-icon>Files
                </div>
                <v-row align="center">
                    <v-col cols="12" md="4">
                        <v-file-input
                            v-model="pdfFile"
                            outlined
                            dense
                            hide-details="auto"
                            accept=".pdf"
                            label="Match sheet (pdf)"
                            prepend-icon=""
                            prepend-inner-icon="mdi-file-pdf-box"
                            :clearable="clearable"
                            @change="changePdfFile"
                            @click:clear="clearPdfFile()"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-file-input
                            v-model="csvFileTeam1"
                            outlined
                            dense
                            hide-details="auto"
                            accept=".csv,.txt"
                            show-size
                            :label="`${match.team1 && match.team1.id ? match.team1.name : 'Team 1'} stats (csv)`"
                            prepend-icon=""
                            prepend-inner-icon="mdi-file-delimited-outline"
                            :clearable="clearable"
                            @change="changeCsvFileTeam1"
                            @click:clear="clearCsvFileTeam1()"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-file-input
                            v-model="csvFileTeam2"
                            outlined
                            dense
                            hide-details="auto"
                            accept=".csv,.txt"
                            show-size
                            :label="`${match.team2 && match.team2.id ? match.team2.name : 'Team 2'} stats (csv)`"
                            prepend-icon=""
                            prepend-inner-icon="mdi-file-delimited-outline"
                            :clearable="clearable"
                            @change="changeCsvFileTeam2"
                            @click:clear="clearCsvFileTeam2()"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card>
        <v-card class="form-card mt-4" elevation="1">
            <div class="d-flex align-center flex-wrap mb-4">
                <v-icon class="mr-2">mdi-account-tie-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">{{ isAdmin ? 'Referees, staff & finance' : 'Referees & staff' }}</span>
                <span class="text-caption grey--text ml-2">optional</span>
                <v-chip v-if="isAdmin && match.staff && match.staff.length" class="ml-3" x-small dark color="grey darken-1">
                    Staff € {{ formatAmount(staffTotal) }}
                </v-chip>
            </div>
            <div class="match-officials-title">
                <v-icon small>mdi-whistle-outline</v-icon>Referees
            </div>
            <amount-rows
                v-model="match.referees"
                :items="referees"
                key-field="refereeId"
                label-field="refereeName"
                amount-field="score"
                select-label="Referee"
                amount-label="Score (0 = not rated)"
                add-label="Add referee"
                empty-text="No referees on this match."
                icon="mdi-whistle-outline"
                prefix=""
                :step="1"
                :max="10"
                :readonly="readOnly"
                :default-amount="() => 0"
            />
            <div class="match-officials-title mt-5">
                <v-icon small>mdi-account-tie-outline</v-icon>Staff
            </div>
            <div v-if="!readOnly && isAdmin" class="text-caption grey--text text--darken-1 mb-3">
                The payment is filled in from the member's rate for the match season (0 for members on a monthly salary) and can be changed for this match only.
            </div>
            <amount-rows
                v-model="match.staff"
                :items="staffMembers"
                key-field="staffId"
                label-field="staffName"
                amount-field="payment"
                select-label="Staff member"
                amount-label="Payment"
                add-label="Add staff member"
                empty-text="No staff on this match."
                icon="mdi-account-outline"
                :readonly="readOnly"
                :hide-amount="!isAdmin"
                :default-amount="staffRate"
            />
            <div v-if="matchFinance" class="match-finance">
                <div class="match-finance__item">
                    <span>Estimated revenue</span>
                    <b>{{ matchFinance.revenue === null ? '—' : `€ ${formatAmount(matchFinance.revenue)}` }}</b>
                </div>
                <div class="match-finance__item">
                    <span>Estimated cost <small>({{ matchFinance.arenaCostSource }} + staff)</small></span>
                    <b>€ {{ formatAmount(matchFinance.cost) }}</b>
                </div>
                <div class="match-finance__item">
                    <span>Result</span>
                    <b :class="matchFinance.result === null ? '' : matchFinance.result >= 0 ? 'green--text text--darken-2' : 'red--text text--darken-1'">
                        {{ matchFinance.result === null ? '—' : `€ ${formatAmount(matchFinance.result)}` }}
                    </b>
                </div>
            </div>
        </v-card>
        <v-card class="form-card mt-4" elevation="1">
            <div class="d-flex align-center flex-wrap mb-4">
                <v-icon color="red" class="mr-2">mdi-youtube</v-icon>
                <span class="text-subtitle-1 font-weight-bold">YouTube</span>
                <v-chip class="ml-3" x-small dark :color="youtubeVideoId ? 'green' : 'grey'">
                    {{ youtubeVideoId ? 'Stream linked' : 'No stream yet' }}
                </v-chip>
                <v-spacer />
                <v-btn v-if="youtubeVideoId" small text :href="`https://www.youtube.com/watch?v=${youtubeVideoId}`" target="_blank">
                    <v-icon left small>mdi-open-in-new</v-icon>Open on YouTube
                </v-btn>
            </div>
            <v-row>
                <v-col cols="12">
                    <v-text-field
                        v-model="match.videoUrl"
                        outlined
                        dense
                        hide-details="auto"
                        label="Video url"
                        prepend-inner-icon="mdi-link-variant"
                        :readonly="readOnly"
                        :clearable="clearable"
                    />
                </v-col>
                <v-col v-if="!readOnly" cols="12" md="6">
                    <image-upload
                        v-model="thumbnailFile"
                        :src="thumbnailPreview"
                        :label="youtubeVideoId ? 'New thumbnail' : 'Thumbnail (optional)'"
                        hint="1280x720 · JPG/PNG · max 2MB"
                        accept="image/jpeg,image/png"
                        height="180"
                        @change="changeThumbnail"
                    />
                </v-col>
                <v-col v-if="youtubeVideoId" cols="12" md="6">
                    <div class="text-caption current-thumbnail-label">Current thumbnail on YouTube</div>
                    <v-img :src="currentThumbnailUrl" height="180" contain class="current-thumbnail" />
                </v-col>
            </v-row>
            <div v-if="!readOnly" class="d-flex align-center flex-wrap mt-4 youtube-actions">
                <span class="text-caption grey--text text--darken-1">{{ youtubeHelpText }}</span>
                <v-spacer />
                <v-btn
                    v-if="youtubeVideoId"
                    class="white--text"
                    depressed
                    color="red"
                    :disabled="!thumbnailFile"
                    :loading="youtubeLoading"
                    @click="requestYoutubeAccess('thumbnail')"
                >
                    <v-icon left>mdi-image-refresh-outline</v-icon>Update thumbnail
                </v-btn>
                <v-btn
                    v-else
                    class="white--text"
                    depressed
                    color="red"
                    :loading="youtubeLoading"
                    @click="requestYoutubeAccess('stream')"
                >
                    <v-icon left>mdi-youtube</v-icon>Create YouTube stream
                </v-btn>
            </div>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import matchService from '@/services/match'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import arenaService from '@/services/arena'
    import { arenaLabel, selectableArenas } from '@/utils/arenas'
    import teamService from '@/services/team'
    import seasonService from '@/services/season'
    import leagueService from '@/services/league'
    import cupService from '@/services/cup'
    import staffService from '@/services/staff'
    import refereeService from '@/services/referee'
    import AmountRows from '@/components/AmountRows'

    export default {
        name: 'MatchForm',
        components: { Alert, AmountRows, DateTimePicker, PageHeader, ImageUpload },
        data: () => {
            return {
                showAlert: false,
                matchDate: null,
                matchTime: null,
                message: null,
                type: null,
                loading: false,
                test: {},
                mode: null,
                readOnly: false,
                clearable: true,
                match: {
                    arena: {
                        name: ''
                    },
                    season: {
                        name: ''
                    },
                    team1: {
                        name: ''
                    },
                    team2: {
                        name: ''
                    },
                    staff: [],
                    referees: []
                },
                staffMembers: [],
                referees: [],
                // finance settings of the match season (with its per-arena costs), loaded when the season changes
                seasonFinance: null,
                durationTypes: ['DAILY', 'WEEKLY'],
                promoTypes: ['SLOTS', 'PS_BATTLES', 'LIVE_CASINO', 'PS_CHALLENGE', 'ROULETTE'],
                matchId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                pdfFile: null,
                thumbnailFile: null,
                thumbnailPreview: '',
                // bumped after a thumbnail upload so the YouTube preview is re-fetched
                thumbnailVersion: 0,
                youtubeLoading: false,
                // which YouTube call to run once Google returns an access token: 'stream' | 'thumbnail' | 'update'
                pendingYoutubeAction: null,
                // the title and start time of the linked stream as the match was loaded (see youtubeDetails)
                youtubeSaved: null,
                csvFileTeam1: null,
                csvFileTeam2: null,
                pdfFileChanged: false,
                csvFileTeam1Changed: false,
                csvFileTeam2Changed: false,
                arenas: [],
                // the arena the match was saved with: it stays on offer if it is changed or cleared in the form
                savedArenaId: null,
                teams: [],
                seasons: [],
                leagues: [],
                cupLeagues: [],
                tokenClient: null
            }
        },
        computed: {
            // video id out of the stored url (embed/<id>, watch?v=<id>, youtu.be/<id>, live/<id>)
            youtubeVideoId() {
                const url = this.match.videoUrl
                if (!url) {
                    return null
                }
                const match = url.match(/(?:youtube\.com\/(?:embed\/|live\/|watch\?(?:.*&)?v=)|youtu\.be\/)([\w-]{11})/)
                return match ? match[1] : null
            },
            currentThumbnailUrl() {
                return `https://i.ytimg.com/vi/${this.youtubeVideoId}/hqdefault.jpg?v=${this.thumbnailVersion}`
            },
            youtubeHelpText() {
                if (this.youtubeVideoId) {
                    return 'Pick a new image and press "Update thumbnail" to replace it on YouTube. Saving new teams, phase, round or date also updates the title and the start time of the stream.'
                }
                const action = this.matchId ? 'Schedules the live stream' : 'Saves the match and schedules the live stream'
                return action + (this.thumbnailFile ? ' with the selected thumbnail.' : '. Add a thumbnail above to upload it too.')
            },
            // Only active teams can be picked, but keep the match's current teams so older matches still display them
            isAdmin() {
                return this.$store.getters.isAdmin
            },
            staffTotal() {
                return (this.match.staff || []).reduce((sum, row) => sum + (Number(row.payment) || 0), 0)
            },
            // Only active arenas can be picked, but keep the match's own arena so older matches still display it
            arenaOptions() {
                return selectableArenas(this.arenas, [this.savedArenaId, this.match.arena && this.match.arena.id])
            },
            // estimate for this match: season revenue, arena cost of the season (or its default) + staff payments
            matchFinance() {
                const season = this.seasonFinance
                if (!this.isAdmin || !season || !this.match.season || season.id !== this.match.season.id) return null
                const arenaId = this.match.arena && this.match.arena.id
                const arenaCost = (season.arenaCosts || []).find(row => row.arenaId === arenaId)
                const venueCost = arenaCost ? arenaCost.cost : season.defaultMatchCost
                const revenue = season.matchRevenue === undefined ? null : season.matchRevenue
                const cost = (Number(venueCost) || 0) + this.staffTotal
                let arenaCostSource = 'default arena cost'
                if (arenaCost) {
                    arenaCostSource = 'arena cost'
                } else if (venueCost === null || venueCost === undefined) {
                    arenaCostSource = 'no arena cost set'
                }
                return { revenue, cost, result: revenue === null ? null : revenue - cost, arenaCostSource }
            },
            // the teams of the chosen league (or cup league) only; every team while none is chosen
            leagueTeams() {
                if (this.match.cup) {
                    const cupId = this.match.cupLeague && this.match.cupLeague.id
                    return cupId ? this.teams.filter(team => team.cupLeague && team.cupLeague.id === cupId) : this.teams
                }
                const leagueId = this.match.league && this.match.league.id
                if (!leagueId) return this.teams
                return this.teams.filter(team => (team.leagues || (team.league ? [team.league] : [])).some(league => league.id === leagueId))
            },
            // the match's current teams stay pickable, so an older match still shows them
            teamOptions() {
                const selected = [this.match.team1, this.match.team2].filter(team => team && team.id && !this.leagueTeams.some(t => t.id === team.id))
                return selected.concat(this.leagueTeams)
            }
        },
        watch: {
            'match.team1': {
                handler() {
                    this.setLeague()
                }
            },
            'match.team2': {
                handler() {
                    this.setLeague()
                }
            },
            'match.cup': {
                handler() {
                    this.setLeague()
                }
            },
            'match.season.id': {
                handler(seasonId) {
                    this.fetchSeasonFinance(seasonId)
                },
                immediate: true
            }
        },
        mounted() {
            // Wait for GIS to be available
            const init = () => {
                if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
                    setTimeout(init, 100)
                    return
                }
                this.tokenClient = window.google.accounts.oauth2.initTokenClient({
                    client_id: '531403802465-cs84qnkb8lgkaoldt56k9655km3b4n0v.apps.googleusercontent.com',
                    scope: 'https://www.googleapis.com/auth/youtube',
                    callback: (resp) => {
                        if (this.pendingYoutubeAction === 'update') {
                            this.submit(resp.error ? null : resp.access_token, resp.error ? `Google sign-in failed: ${resp.error}` : null)
                            return
                        }
                        if (resp.error) {
                            this.youtubeLoading = false
                            this.setAlert(`Google sign-in failed: ${resp.error}`, 'error')
                            return
                        }
                        if (this.pendingYoutubeAction === 'thumbnail') {
                            this.updateThumbnail(resp.access_token)
                        } else {
                            this.createStream(resp.access_token)
                        }
                    },
                    // popup closed or blocked
                    error_callback: () => {
                        if (this.pendingYoutubeAction === 'update') {
                            this.submit(null, 'Google sign-in was cancelled')
                            return
                        }
                        this.youtubeLoading = false
                    }
                })
            }
            init()
        },
        created() {
            this.mode = this.$route.params.mode
            this.fetchAllArenas()
            this.fetchAllTeams()
            this.fetchAllSeasons()
            this.fetchAllLeagues()
            this.fetchAllStaff()
            this.fetchAllReferees()
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.matchId = this.$route.params.matchId
            if (this.matchId) {
                matchService.getMatch(this.matchId).then(resp => {
                    this.match = { ...resp.data, staff: resp.data.staff || [], referees: resp.data.referees || [] }
                    this.savedArenaId = resp.data.arena ? resp.data.arena.id : null
                    this.pdfFile = this.match.file
                    if (this.match.matchDatetime === null) {
                        this.matchDate = null
                        this.matchTime = null
                    } else {
                        let matchDate = momentTimezone(this.match.matchDatetime).tz('Europe/Athens').format('YYYY-MM-DD[T]HH:mm:ss').toString()
                        matchDate = matchDate.split('T')
                        this.matchDate = matchDate[0]
                        this.matchTime = matchDate[1]
                    }
                    this.youtubeSaved = this.youtubeDetails()
                })
            }
        },
        methods: {
            arenaLabel,
            changeThumbnail(file) {
                if (file && file.size > 2 * 1024 * 1024) {
                    this.thumbnailFile = null
                    this.thumbnailPreview = ''
                    this.setAlert('Thumbnail must be less than 2MB', 'error')
                    return
                }
                this.thumbnailPreview = file ? URL.createObjectURL(file) : ''
            },
            requestYoutubeAccess(action) {
                if (action === 'stream') {
                    const missing = []
                    if (!this.match.team1 || !this.match.team1.id || !this.match.team2 || !this.match.team2.id) {
                        missing.push('both teams')
                    }
                    if (!this.matchDate || !this.matchTime) {
                        missing.push('match date and time')
                    }
                    if (missing.length) {
                        this.setAlert(`Set ${missing.join(' and ')} before creating the stream`, 'error')
                        return
                    }
                }
                this.pendingYoutubeAction = action
                this.youtubeLoading = true
                // must run straight from the click, otherwise the browser blocks the Google popup
                this.tokenClient.requestAccessToken()
            },
            async createStream(accessToken) {
                try {
                    // a new match has no id yet and the broadcast endpoint needs one, so save it first
                    if (!this.matchId) {
                        await this.saveMatch()
                    }
                    const resp = await matchService.createYoutubeLive({
                        accessToken: accessToken,
                        title: this.getTitle(),
                        startTime: momentTimezone(`${this.matchDate} ${this.matchTime}`).tz('Europe/Athens').utc().toISOString()
                    }, this.matchId)
                    const broadcastId = JSON.parse(resp.data.content).id
                    this.match.videoUrl = `https://www.youtube.com/embed/${broadcastId}`
                    let thumbnailError = null
                    if (this.thumbnailFile) {
                        try {
                            await this.uploadThumbnail(broadcastId, accessToken)
                        } catch (error) {
                            thumbnailError = error.message
                        }
                    }
                    await this.editMatch()
                    if (thumbnailError) {
                        this.setAlert(`Youtube stream scheduled, but the thumbnail upload failed: ${thumbnailError}`, 'error')
                        return
                    }
                    this.setAlert('Youtube stream scheduled successfully', 'success')
                    this.$router.push(`/matches`)
                } catch (error) {
                    this.setAlert(this.errorMessage(error), 'error')
                } finally {
                    this.youtubeLoading = false
                }
            },
            async updateThumbnail(accessToken) {
                try {
                    await this.uploadThumbnail(this.youtubeVideoId, accessToken)
                    this.thumbnailFile = null
                    this.thumbnailPreview = ''
                    this.thumbnailVersion++
                    this.setAlert('Thumbnail updated on YouTube', 'success')
                } catch (error) {
                    this.setAlert(`Thumbnail upload failed: ${error.message}`, 'error')
                } finally {
                    this.youtubeLoading = false
                }
            },
            errorMessage(error) {
                return (error.response && error.response.data && error.response.data.message) || error.message || 'Something went wrong'
            },
            uploadThumbnail(videoId, accessToken) {
                return fetch(`https://www.googleapis.com/upload/youtube/v3/thumbnails/set?videoId=${videoId}&uploadType=media`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': this.thumbnailFile.type
                    },
                    body: this.thumbnailFile
                }).then(async(resp) => {
                    if (!resp.ok) {
                        const body = await resp.json().catch(() => null)
                        throw new Error(body?.error?.message || `HTTP ${resp.status}`)
                    }
                })
            },
            getTitle() {
                let title = ``
                if (this.match.cup) {
                    title = `${this.match.team1.name} vs ${this.match.team2.name} (Round${this.match.round} - ${this.match.cupLeague ? this.match.cupLeague.title : 'Cup'} - `
                } else if (this.match.phase === 'placements') {
                    title = `${this.match.team1.name} vs ${this.match.team2.name} (Προκριματικά - Round${this.match.round} - ${this.match.league ? this.match.league.title : ''} - `
                } else if (this.match.phase === 'playoffs') {
                    title = `${this.match.team1.name} vs ${this.match.team2.name} (Playoffs - Round${this.match.round} - ${this.match.league ? this.match.league.title : ''} - `
                } else if (this.match.phase === 'regular_season') {
                    title = `${this.match.team1.name} vs ${this.match.team2.name} (Regular Season - Round${this.match.round} - ${this.match.league ? this.match.league.title : ''} - `
                }
                title += `${momentTimezone(`${this.matchDate} ${this.matchTime}`).tz('Europe/Athens').format('DD/MM/YYYY').toString()})`
                return title
            },
            // teams can play in more than one league: the league is picked when they share exactly one
            setLeague() {
                if (!this.match.team1 || !this.match.team2) {
                    return
                }
                if (this.match.cup) {
                    this.match.league = null
                    const cup1 = this.match.team1.cupLeague
                    const cup2 = this.match.team2.cupLeague
                    if (cup1 && cup2 && cup1.id === cup2.id) {
                        this.match.cupLeague = cup1
                    }
                } else {
                    this.match.cupLeague = null
                    const leagues1 = this.match.team1.leagues || (this.match.team1.league ? [this.match.team1.league] : [])
                    const leagues2 = this.match.team2.leagues || (this.match.team2.league ? [this.match.team2.league] : [])
                    const common = leagues1.filter(league1 => leagues2.some(league2 => league2.id === league1.id))
                    if (common.length === 1) {
                        this.match.league = common[0]
                    }
                }
            },
            changeCsvFileTeam1() {
                this.csvFileTeam1Changed = true
                // if (this.csvFileTeam1) {
                //     this.csvFileTeam1 = URL.createObjectURL(this.csvFileTeam1)
                // }
            },
            clearCsvFileTeam1() {
                this.csvFileTeam1Path = ''
            },
            changeCsvFileTeam2() {
                this.csvFileTeam2Changed = true
                // if (this.csvFileTeam2) {
                //     this.csvFileTeam2 = URL.createObjectURL(this.csvFileTeam2)
                // }
            },
            clearCsvFileTeam2() {
                this.csvFileTeam2Path = ''
            },
            changePdfFile() {
                this.pdfFileChanged = true
                if (this.pdfFile) {
                    this.pdfFilePath = URL.createObjectURL(this.pdfFile)
                }
            },
            clearPdfFile() {
                this.pdfFilePath = ''
                this.match.file = null
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            changeImage() {
                this.imageChanged = true
                this.imagePath = URL.createObjectURL(this.image)
            },
            changeIcon() {
                this.iconChanged = true
                this.iconPath = URL.createObjectURL(this.icon)
            },
            setMultilingualConfig(data) {
                this.match.multilingualConfigs = data
            },
            shouldDisableSubmit() {
                return false
            },
            setMatchDate(date) {
                this.matchDate = date
            },
            setMatchTime(time) {
                this.matchTime = time
            },
            cleaMatchDateTime() {
                this.matchDate = null
                this.matchTime = null
            },
            createMatch() {
                return matchService.createMatch(this.match).then((resp) => {
                    this.matchId = resp.data.id
                    this.loading = false
                })
            },
            editMatch() {
                return matchService.updateMatch(this.matchId, this.match).then(() => {
                    this.loading = false
                })
            },
            // saves the form (pdf, match, csv stats) without leaving the page
            saveMatch() {
                if (this.matchDate && this.matchTime) {
                    this.match.matchDatetime = momentTimezone(`${this.matchDate} ${this.matchTime}`).tz('Europe/Athens').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString()
                } else {
                    delete this.match.matchDatetime
                }
                return Promise.resolve(this.uploadPdf())
                    .then(() => this.matchId ? this.editMatch() : this.createMatch())
                    .then(() => this.uploadCsvs(this.matchId, this.csvFileTeam1, this.match.team1?.id, this.csvFileTeam1Changed))
                    .then(() => this.uploadCsvs(this.matchId, this.csvFileTeam2, this.match.team2?.id, this.csvFileTeam2Changed))
                    .then(() => {
                        this.pdfFileChanged = false
                        this.csvFileTeam1Changed = false
                        this.csvFileTeam2Changed = false
                    })
            },
            onSubmit() {
                // a change of the title or the start time of the linked stream: Google first (straight from
                // the click, else the browser blocks the popup), then the match is saved and the stream updated
                if (this.needsYoutubeUpdate() && this.tokenClient) {
                    this.loading = true
                    this.youtubeLoading = true
                    this.pendingYoutubeAction = 'update'
                    this.tokenClient.requestAccessToken()
                    return
                }
                this.submit()
            },
            // accessToken: update the stream on YouTube too; youtubeSkipped: why it is not updated
            submit(accessToken = null, youtubeSkipped = null) {
                this.pendingYoutubeAction = null
                this.loading = true
                // an empty score means not rated
                this.match.referees = (this.match.referees || []).map(row => ({ ...row, score: Math.min(10, Math.max(0, Math.round(Number(row.score) || 0))) }))
                this.saveMatch()
                    .then(() => {
                        if (!accessToken) return youtubeSkipped
                        return this.updateBroadcast(accessToken).then(() => null, error => error.message)
                    })
                    .then((youtubeError) => {
                        if (youtubeError) {
                            // the match is saved: stay to show why the stream is not
                            this.loading = false
                            this.setAlert(`Match saved, but the YouTube stream was not updated: ${youtubeError}`, 'error')
                            return
                        }
                        this.$router.push(`/matches`)
                    })
                    .catch((error) => {
                        this.setAlert(this.errorMessage(error), 'error')
                        this.loading = false
                    })
                    .finally(() => {
                        this.youtubeLoading = false
                    })
            },
            // what the stream on YouTube shows of the match; null without both teams and the date
            youtubeDetails() {
                if (!this.match.team1 || !this.match.team1.name || !this.match.team2 || !this.match.team2.name || !this.matchDate || !this.matchTime) {
                    return null
                }
                return {
                    videoId: this.youtubeVideoId,
                    title: this.getTitle(),
                    startTime: momentTimezone(`${this.matchDate} ${this.matchTime}`).tz('Europe/Athens').utc().toISOString()
                }
            },
            // a saved match whose stream (the same one as loaded) would show another title or start time
            needsYoutubeUpdate() {
                if (!this.matchId || !this.youtubeVideoId) return false
                const current = this.youtubeDetails()
                if (!current) return false
                const saved = this.youtubeSaved
                if (saved && saved.videoId !== current.videoId) return false
                return !saved || saved.title !== current.title || saved.startTime !== current.startTime
            },
            // a call to the YouTube API; throws the message of YouTube on an error
            youtubeRequest(url, accessToken, method = 'GET', body = null) {
                return fetch(`https://www.googleapis.com/youtube/v3/${url}`, {
                    method,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        ...(body ? { 'Content-Type': 'application/json' } : {})
                    },
                    body: body ? JSON.stringify(body) : null
                }).then(async(resp) => {
                    const data = await resp.json().catch(() => null)
                    if (!resp.ok) {
                        throw new Error(data?.error?.message || `HTTP ${resp.status}`)
                    }
                    return data
                })
            },
            // a stream not started yet: its title and start time; live or over: the title of its video only
            // (the start time cannot change any more). A field not sent would be cleared, so they are sent as they are
            async updateBroadcast(accessToken) {
                const details = this.youtubeDetails()
                const id = details.videoId
                const found = await this.youtubeRequest(`liveBroadcasts?part=snippet,status&id=${id}`, accessToken)
                const broadcast = found && found.items && found.items[0]
                if (broadcast && ['created', 'ready', 'testStarting', 'testing'].includes(broadcast.status.lifeCycleStatus)) {
                    const snippet = { title: details.title, scheduledStartTime: details.startTime, description: broadcast.snippet.description || '' }
                    if (broadcast.snippet.scheduledEndTime) {
                        snippet.scheduledEndTime = broadcast.snippet.scheduledEndTime
                    }
                    await this.youtubeRequest('liveBroadcasts?part=snippet', accessToken, 'PUT', { id, snippet })
                } else {
                    const videos = await this.youtubeRequest(`videos?part=snippet&id=${id}`, accessToken)
                    const video = videos && videos.items && videos.items[0]
                    if (!video) {
                        throw new Error('the video is not on the YouTube account signed in')
                    }
                    const snippet = { title: details.title, categoryId: video.snippet.categoryId, description: video.snippet.description || '' }
                    for (const key of ['tags', 'defaultLanguage', 'defaultAudioLanguage']) {
                        if (video.snippet[key]) snippet[key] = video.snippet[key]
                    }
                    await this.youtubeRequest('videos?part=snippet', accessToken, 'PUT', { id, snippet })
                }
                this.youtubeSaved = details
            },
            uploadCsvs(matchId, csvFileTeam, teamId, csvFileChanged) {
                if (csvFileChanged && csvFileTeam) {
                    let formData = new FormData()
                    formData.append('file', csvFileTeam)
                    return matchService.importCsvStats(matchId, teamId, formData).then(() => {
                        this.loading = false
                    })
                }
            },
            uploadPdf() {
                if (!this.pdfFileChanged || this.pdfFile == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.pdfFile)
                return matchService.uploadFile(formData, 'pdf').then((response) => {
                    this.match.file = response.data.content
                })
            },
            fetchAllArenas() {
                arenaService.getArenas().then(resp => {
                    this.arenas = resp.data.content
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            fetchAllTeams() {
                teamService.getFilteredTeams({ active: true }).then(resp => {
                    this.teams = resp.data.content
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            fetchAllSeasons() {
                seasonService.getSeasons().then(resp => {
                    this.seasons = resp.data.content
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            fetchAllStaff() {
                staffService.getAllStaff().then(resp => {
                    this.staffMembers = resp.data
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            fetchAllReferees() {
                refereeService.getAllReferees().then(resp => {
                    this.referees = resp.data.content.slice().sort((a, b) => a.name.localeCompare(b.name))
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            fetchSeasonFinance(seasonId) {
                if (!seasonId || !this.isAdmin) {
                    this.seasonFinance = null
                    return
                }
                seasonService.getSeason(seasonId).then(resp => {
                    // ignore a late answer for a season that is no longer selected
                    if (this.match.season && this.match.season.id === seasonId) {
                        this.seasonFinance = resp.data
                    }
                })
            },
            // the member's payment per match for the match season, if one is set (0 for a monthly salary)
            staffRate(staffId) {
                const member = this.staffMembers.find(staff => staff.id === staffId)
                const seasonId = this.match.season && this.match.season.id
                const rate = member && seasonId && (member.seasonPayments || []).find(payment => payment.seasonId === seasonId)
                if (!rate) return null
                return rate.monthly ? 0 : rate.paymentPerMatch
            },
            formatAmount(value) {
                return Number(value || 0).toLocaleString('el-GR', { maximumFractionDigits: 2 })
            },
            fetchAllLeagues() {
                cupService.getAllCups().then(resp => {
                    this.cupLeagues = resp.data
                })
                leagueService.getAllLeagues().then(resp => {
                    this.leagues = resp.data
                }).catch((error) => {
                    this.setAlert(error.response.data.message, 'error')
                })
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.match-officials-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 10px;
}

.match-finance {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.match-finance__item {
    flex: 1 1 160px;
    display: flex;
    flex-direction: column;
    padding: 10px 14px;
    border-radius: 8px;
    background: #f7f7f8;
}

.match-finance__item span {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.match-finance__item b {
    font-size: 18px;
}

.current-thumbnail-label {
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
}

.current-thumbnail {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #000;
}

.youtube-actions {
    gap: 8px 16px;
}

.score-input ::v-deep input {
    text-align: center;
    font-weight: 700;
}

.score-row {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fafafa;
}
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
